/**
 * Demo Mode Progress Tracking
 * Manages feature discovery progress for demo mode users
 */

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

export interface DemoFeatureProgress {
  viewedAnalytics: boolean;
  createdChart: boolean;
  createdReport: boolean;
  lastUpdated: number;
}

export const initialDemoProgress: DemoFeatureProgress = {
  viewedAnalytics: false,
  createdChart: false,
  createdReport: false,
  lastUpdated: Date.now(),
};

/**
 * Main progress atom - synced with localStorage
 * Automatically syncs across tabs via Jotai atomWithStorage
 */
export const demoProgressAtom = atomWithStorage<DemoFeatureProgress>(
  'demo_feature_progress_v1',
  initialDemoProgress
);

/**
 * Computed atom: Progress count (0-3)
 */
export const demoProgressCountAtom = atom(get => {
  const progress = get(demoProgressAtom);
  let count = 0;
  if (progress.viewedAnalytics) count += 1;
  if (progress.createdChart) count += 1;
  if (progress.createdReport) count += 1;
  return count;
});

/**
 * Computed atom: Progress percentage (0-100)
 */
export const demoProgressPercentAtom = atom(get => {
  const count = get(demoProgressCountAtom);
  return Math.round((count / 3) * 100);
});

/**
 * Computed atom: Is progress complete (all 3 features discovered)
 */
export const isDemoProgressCompleteAtom = atom(get => {
  const count = get(demoProgressCountAtom);
  return count === 3;
});

/**
 * Computed atom: Next feature to discover
 */
export const nextDemoFeatureAtom = atom(get => {
  const progress = get(demoProgressAtom);
  if (!progress.viewedAnalytics) return 'viewAnalytics';
  if (!progress.createdChart) return 'createChart';
  if (!progress.createdReport) return 'createReport';
  return null;
});

/**
 * Write atom: Mark analytics as viewed
 */
export const markAnalyticsViewedAtom = atom(null, (get, set) => {
  const current = get(demoProgressAtom);
  set(demoProgressAtom, {
    ...current,
    viewedAnalytics: true,
    lastUpdated: Date.now(),
  });
});

/**
 * Write atom: Mark chart as created
 */
export const markChartCreatedAtom = atom(null, (get, set) => {
  const current = get(demoProgressAtom);
  set(demoProgressAtom, {
    ...current,
    createdChart: true,
    lastUpdated: Date.now(),
  });
});

/**
 * Write atom: Mark report as created
 */
export const markReportCreatedAtom = atom(null, (get, set) => {
  const current = get(demoProgressAtom);
  set(demoProgressAtom, {
    ...current,
    createdReport: true,
    lastUpdated: Date.now(),
  });
});

/**
 * Write atom: Reset all progress
 */
export const resetDemoProgressAtom = atom(null, (get, set) => {
  set(demoProgressAtom, {
    ...initialDemoProgress,
    lastUpdated: Date.now(),
  });
});
